import { supabase } from "./supabase";

/** Crée une invitation pour un dossier */
export async function createInvitation(
  dossierId: string,
  email: string
): Promise<{ token: string; inviteLink: string }> {
  const { data, error } = await supabase
    .from("invitations")
    .insert({ dossier_id: dossierId, email: email.toLowerCase().trim() })
    .select("token")
    .single();

  if (error) throw error;

  const baseUrl = window.location.origin;
  const inviteLink = `${baseUrl}/auth/invite?token=${data.token}`;
  return { token: data.token, inviteLink };
}

/** Envoie l'email d'invitation via l'Edge Function Supabase (Resend) */
export async function sendInvitationEmail(
  email: string,
  inviteLink: string,
  dossierNom: string,
  dossierId: string
): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !anonKey) {
    throw new Error("Configuration Supabase manquante (VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY)");
  }

  const { data: sessionData } = await supabase.auth.getSession();
  let token = sessionData.session?.access_token;

  if (!token) {
    const { data: refreshed } = await supabase.auth.refreshSession();
    token = refreshed.session?.access_token;
  }

  if (!token) {
    throw new Error("Vous devez être connecté pour envoyer une invitation.");
  }

  const url = `${supabaseUrl}/functions/v1/send-invitation-email`;
  const body = JSON.stringify({ email, inviteLink, dossierNom, dossier_id: dossierId });

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        apikey: anonKey,
      },
      body,
    });
  } catch (err) {
    const msg =
      err instanceof TypeError && err.message?.includes("fetch")
        ? "Impossible de joindre le serveur. Vérifiez VITE_SUPABASE_URL (ex: https://xxx.supabase.co) et votre connexion."
        : err instanceof Error
          ? err.message
          : "Erreur réseau.";
    throw new Error(msg);
  }

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const msg =
      data?.error ||
      data?.message ||
      (response.status === 401 ? "Session expirée. Reconnectez-vous." : null) ||
      (response.status === 500 ? "Erreur serveur. Vérifiez RESEND_API_KEY dans Supabase." : null) ||
      (response.status === 0 ? "Connexion refusée (CORS ou réseau)." : null) ||
      `Erreur ${response.status}`;
    throw new Error(msg);
  }

  if (data?.error) throw new Error(data.error);
}

/** Récupère une invitation par token */
export async function getInvitationByToken(
  token: string
): Promise<{ id: string; dossier_id: string; email: string; dossier_nom: string } | null> {
  const { data, error } = await supabase.rpc("get_invitation_by_token", {
    p_token: token,
  });
  if (error || !data || data.length === 0) return null;
  const row = data[0];
  return {
    id: row.id,
    dossier_id: row.dossier_id,
    email: row.email,
    dossier_nom: row.dossier_nom || "Dossier",
  };
}

/** Utilise une invitation : lie l'utilisateur au dossier (auth.uid() côté SQL). */
export async function useInvitation(token: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("use_invitation", {
    p_token: token,
  });
  if (error) throw error;
  return data === true;
}
